import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Route as RouterRoute, Redirect, useRouteMatch } from 'react-router-dom';
import { userSelector, networksSelector, activeNetworkSelector } from 'redux/selectors';
import * as Sentry from '@sentry/react';

import get from 'helpers/get';
import { setRole, mapRoleToPermissions } from './permissions';

import { sessionRoutes } from './sessionRoutes';

const VISIBILITY_OPTIONS = ['public', 'user', 'onboarding', 'all'];
const EXCLUDE_ROUTES = ['/stay-tuned'];

const Route = ({ component: Component, visibility, path, ...rest }) => {
  const user = useSelector(userSelector);
  const networksStore = useSelector(networksSelector);
  const userLoggedIn = !!user.id;
  const { networks } = networksStore;
  const activeNetwork = useSelector(activeNetworkSelector);
  const pathMatch = useRouteMatch(path);

  const shouldOnboard = useMemo(() => {
    if (!user?.onboardingCompleted) return true;

    if (!activeNetwork) return false;

    const hasConnect = activeNetwork.connect?.hasAccess;
    const hasGoals = activeNetwork.userGoalsAnswered;

    if (hasConnect && (!activeNetwork.kind || !hasGoals)) return true;

    return false;
  }, [activeNetwork, user]);

  const role = setRole({
    isOnlyAlly: user.isOnlyAlly,
    authenticated: userLoggedIn,
    confirmed: user.confirmed,
    onboardingCompleted: !shouldOnboard,
  });

  const permissions = mapRoleToPermissions({
    role,
    user,
    oauth: !!user?.oauthProviders?.length,
    networkSlug: activeNetwork?.slug || Object.values(networks ?? {})?.[0]?.slug,
    network: activeNetwork,
    pathMatch,
  });

  const renderRoute = (props) => {
    let excludedRoute = EXCLUDE_ROUTES.includes(path);
    if (Array.isArray(path)) {
      // eslint-disable-next-line react/prop-types
      excludedRoute = path.some((p) => EXCLUDE_ROUTES.includes(p));
    }

    if (
      userLoggedIn &&
      !excludedRoute &&
      (!networks || !Object.keys(networks).length) &&
      user.isOnlyAlly &&
      !user?.allyingCollections?.length
    ) {
      return <Redirect to="/stay-tuned" />;
    }

    let visibilities = visibility;
    if (!Array.isArray(visibility)) {
      visibilities = [visibility];
    }

    // allow job board routes for company networks
    if (
      visibilities.some((e) => e === 'job_board') &&
      !activeNetwork.features.includes('job_board') &&
      !activeNetwork.isCompany
    ) {
      return <Redirect to="/not-found" />;
    }

    if (permissions.routes.some((e) => visibilities.includes(e))) {
      return <Component {...props} />;
    }

    // Redirect to previous URL after login
    const fromPath = get(props, 'location.state.from.pathname');
    const fromSearch = get(props, 'location.state.from.search');

    // eslint-disable-next-line react/prop-types
    if (props.location.pathname === sessionRoutes.login.path && fromPath) {
      return (
        <Redirect
          to={{
            pathname: fromPath,
            search: fromSearch,
            // eslint-disable-next-line react/prop-types
            state: { from: props.location },
          }}
        />
      );
    }

    return (
      <Redirect
        to={{
          pathname: permissions.home,
          // eslint-disable-next-line react/prop-types
          state: { from: props.location },
        }}
      />
    );
  };

  return <RouterRoute {...rest} path={path} render={renderRoute} />;
};

Route.propTypes = {
  component: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  visibility: (props, propName, componentName) => {
    // eslint-disable-next-line react/destructuring-assignment
    const propValues = props[propName];

    const isArray = Array.isArray(propValues);
    const isString = typeof propValues === 'string';

    const error = `\`${componentName}\` visibility prop only accept Array or string with this possible values: ${VISIBILITY_OPTIONS.join(
      ',',
    )}`;

    if (!isString && !isArray) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof propValues}\` supplied to \`${componentName}\`, expected \`string\` or \`array\`.`,
      );
    }

    if (isArray && propValues.some((p) => !VISIBILITY_OPTIONS.includes(p))) {
      return new Error(error);
    }

    if (isString && !VISIBILITY_OPTIONS.includes(propValues)) {
      return new Error(error);
    }

    return null;
  },
  path: PropTypes.string.isRequired,
};

export default Sentry.withSentryRouting(Route);
